import { lessonAtom } from "@core/grades/atoms";
import { formatDuration, formatNumber, money, URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { useMemo } from "react";
import { useIsLoggedIn } from "../../../framework/remix/account/hooks";
import { useAction } from "../../../framework/remix/shared/hooks";
import { paymentAtom, paymentPortal } from "../../checkout/atoms";
import { errorToast, successToast } from "../../design-system/components/toast";
import { getLessonBreadcrumbs } from "../utils/get-lesson-breadcrumbs";

// Lesson Details Hook
export function useLessonDetails() {
  const lesson = lessonAtom.useValue();

  const breadcrumbs = useMemo(() => getLessonBreadcrumbs(lesson), [lesson]);

  return {
    breadcrumbs,
  };
}

// Lesson Header Hook
export function useLessonHeader() {
  const lesson = lessonAtom.useValue();

  const {
    name,
    image,
    instructor,
    rating,
    views,
    total,
    duration,
    isFree,
    cost,
    isPaid,
  } = lesson;

  const formattedDuration = duration ? formatDuration(duration) : null;
  const formattedViews = trans("views", { total: formatNumber(views) });
  const priceText = isFree ? trans("free") : money(cost);
  const priceSubtext = isFree ? trans("noCost") : trans("oneTimePayment");

  return {
    lesson,
    name,
    image,
    instructor,
    rating,
    views,
    total,
    duration,
    isFree,
    cost,
    isPaid,
    formattedDuration,
    formattedViews,
    priceText,
    priceSubtext,
  };
}

// Lesson Quick Actions Hook
export function useLessonQuickActions() {
  const lesson = lessonAtom.value;

  const buttonText = lesson.isFree
    ? trans("startLearning")
    : trans("enrollNow");

  const priceText = lesson.isFree ? trans("free") : money(lesson.cost);
  const priceSubtext = lesson.isFree
    ? trans("noCost")
    : trans("oneTimePayment");

  const handleStartLearning = () => {
    // Scroll to the content tab
    const contentTab = document.querySelector("#lesson-content");
    if (contentTab) {
      contentTab.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return {
    lesson,
    buttonText,
    priceText,
    priceSubtext,
    handleStartLearning,
  };
}

export function usePurchaseLesson() {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const submit = useAction("/invoices");

  const purchaseLesson = async () => {
    if (!isLoggedIn) {
      navigate(URLS.auth.login);
      return;
    }

    const lesson = lessonAtom.value;

    if (lesson.isFree) {
      const data = {
        lessons: [lesson.id],
      };

      const response = await submit(data);

      if (response.isOK) {
        navigate(0); // refresh to fetch the updated lesson
        successToast(trans("grades.lessonUnlocked"));
      } else {
        errorToast(response.error);
      }
      return;
    }

    paymentAtom.change("lessons", [lesson]);
    paymentPortal.open();
  };

  return {
    purchaseLesson,
  };
}

// Lesson Resources Hook
export function useLessonResources() {
  const lesson = lessonAtom.useValue();

  const groupedResources = useMemo(() => {
    if (!lesson?.resources?.length) return {};

    return lesson.resources.reduce(
      (groups, resource) => {
        const category = resource.category || trans("general");
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(resource);
        return groups;
      },
      {} as Record<string, typeof lesson.resources>,
    );
  }, [lesson]);

  return {
    groupedResources,
  };
}
